/* global Word console */

import {
  CanvasChange,
  DocumentContent,
  DocumentElement,
  isCommentContentRange,
  isDocumentElement,
  ParseState,
} from "../types";
import { handleAddElement } from "../operations/add";
import { applyTextChanges } from "./text";

function setRunStyle(
  styleName: "bold" | "italics" | "underline" | "strikethrough",
  value: boolean,
  range: Word.Range | Word.CommentContentRange
) {
  switch (styleName) {
    case "bold":
      if (isCommentContentRange(range)) {
        range.bold = value;
      } else {
        range.font.bold = value;
      }
      break;
    case "italics":
      if (isCommentContentRange(range)) {
        range.italic = value;
      } else {
        range.font.italic = value;
      }
      break;
    case "underline":
      if (isCommentContentRange(range)) {
        range.underline = value ? Word.UnderlineType.single : Word.UnderlineType.none;
      } else {
        range.font.underline = value ? Word.UnderlineType.single : Word.UnderlineType.none;
      }
      break;
    case "strikethrough":
      if (isCommentContentRange(range)) {
        range.strikeThrough = value;
      } else {
        range.font.strikeThrough = value;
      }
      break;
  }
}

export async function addRunsToParagraph(
  paragraph: Word.Paragraph | Word.CommentContentRange,
  els: (DocumentElement | DocumentContent)[],
  doc: Word.Document,
  state: ParseState,
  context: Word.RequestContext
) {
  let range: Word.Range | Word.CommentContentRange | null = null;

  for (const child of els) {
    if (isDocumentElement(child)) {
      if (isCommentContentRange(range)) continue;
      if (child.type === "comment") {
        const newComment = range?.insertComment("");
        await addRunsToParagraph(newComment.contentRange, child.content, doc, state, context);
      } else {
        if (isCommentContentRange(paragraph)) continue;
        handleAddElement(paragraph, doc, child, state, false, [], context);
      }
    } else {
      // Handle content with newlines - insert line breaks to keep content within same paragraph
      const textContent = child.content || "";
      const lines = textContent.split("\n");

      for (let i = 0; i < lines.length; i++) {
        // Insert line break before non-first lines
        if (i > 0 && !isCommentContentRange(paragraph)) {
          const wordParagraph = paragraph as Word.Paragraph;
          const lastRange = wordParagraph.getRange(Word.RangeLocation.end);
          lastRange.insertBreak(Word.BreakType.line, Word.InsertLocation.before);
        }

        if (lines[i]) {
          range = paragraph.insertText(lines[i], Word.InsertLocation.end);
          setRunStyle("bold", child.format.includes("bold"), range);
          setRunStyle("italics", child.format.includes("italic"), range);
          setRunStyle("strikethrough", child.format.includes("strikethrough"), range);
          if (child.href) {
            if (isCommentContentRange(range)) {
              range.insertText("", Word.InsertLocation.end);
              range.hyperlink = child.href;
            } else {
              range.hyperlink = child.href;
            }
          }
        }
      }
    }
  }
}

export async function applyRangeChanges(
  change: CanvasChange,
  context: Word.RequestContext,
  targetRuns: Word.Range[],
  state: ParseState
) {
  let i = 0;
  let childChangeIndex = -1;
  for (const childChange of change.child_changes) {
    childChangeIndex++;
    let currentRange: Word.Range | undefined = targetRuns[i];
    if (typeof childChange.e1 === "string" || typeof childChange.e2 === "string") {
      throw new Error("Child change type mismatch");
    }

    switch (childChange.op) {
      case "=":
        i++;
        break;
      case "+":
        // Add before the run
        if (!isDocumentElement(childChange.e2)) {
          if (currentRange) {
            currentRange = currentRange.insertText(childChange.e2.content, Word.InsertLocation.before);
            currentRange.hyperlink = childChange.e2.href ?? null;
            setRunStyle("bold", childChange.e2.format.includes("bold"), currentRange);
            setRunStyle("italics", childChange.e2.format.includes("italic"), currentRange);
            setRunStyle("strikethrough", childChange.e2.format.includes("strikethrough"), currentRange);
            if (childChangeIndex < change.child_changes.length - 1) {
              const nextChange = change.child_changes[childChangeIndex + 1];
              if (
                nextChange.op === "~" &&
                !isDocumentElement(nextChange.e2) &&
                typeof nextChange.e2 !== "string" &&
                nextChange.e2.content.match(/^\s+/)
              ) {
                console.log("Adding space after run");
                currentRange = currentRange.insertText(" ", Word.InsertLocation.after);
              }
            }
          } else {
            // We're at the end of the paragraph, so we need to add behind the last run
            currentRange = targetRuns[targetRuns.length - 1].insertText(
              childChange.e2.content,
              Word.InsertLocation.after
            );
            currentRange.hyperlink = childChange.e2.href ?? null;
            setRunStyle("bold", childChange.e2.format.includes("bold"), currentRange);
            setRunStyle("italics", childChange.e2.format.includes("italic"), currentRange);
            setRunStyle("strikethrough", childChange.e2.format.includes("strikethrough"), currentRange);
            targetRuns.push(currentRange);
            i++;
          }
        } else if (childChange.e2.type === "comment") {
          if (!currentRange && targetRuns.length > 0) {
            currentRange = targetRuns[targetRuns.length - 1];
          }
          console.log("Inserting comment", childChange.e2.content);
          const newComment = currentRange.insertComment("");
          await addRunsToParagraph(newComment.contentRange, childChange.e2.content, context.document, state, context);
        }
        break;
      case "-":
        if (!currentRange && targetRuns.length > 0) {
          currentRange = targetRuns[targetRuns.length - 1];
        }
        if (isDocumentElement(childChange.e1) && childChange.e1.type === "comment") {
          // Remove comment
          currentRange?.getComments()?.getFirstOrNullObject()?.delete();
        } else {
          currentRange.insertText("", Word.InsertLocation.replace);
          currentRange = undefined;
        }
        i++;
        break;
      case "~":
        if (currentRange && !isDocumentElement(childChange.e2)) {
          currentRange.hyperlink = childChange.e2.href ?? null;
          setRunStyle("bold", childChange.e2.format.includes("bold"), currentRange);
          setRunStyle("italics", childChange.e2.format.includes("italic"), currentRange);
          setRunStyle("strikethrough", childChange.e2.format.includes("strikethrough"), currentRange);
        }
        if (childChange.child_changes.length && currentRange) {
          await applyTextChanges(currentRange, childChange, context);
        } else {
          if (!currentRange && targetRuns.length > 0) {
            currentRange = targetRuns[targetRuns.length - 1];
          }
          if (isDocumentElement(childChange.e2) && childChange.e2.type === "comment") {
            if (!isDocumentElement(childChange.e1) || childChange.e1.type !== "comment") {
              currentRange.insertText("", Word.InsertLocation.replace);
            }
            // Remove old comment
            currentRange.getComments()?.getFirstOrNullObject()?.delete();
            // Insert new comment
            const newComment = currentRange.insertComment("");
            await addRunsToParagraph(newComment.contentRange, childChange.e2.content, context.document, state, context);
          } else if (!isDocumentElement(childChange.e2) && currentRange) {
            currentRange.insertText(childChange.e2.content, Word.InsertLocation.after);
          }
        }
        i++;
        break;
    }
  }
}
